import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  User,
  Network,
  Package as PackageIcon,
  IndianRupee,
  LogOut,
  ChevronDown,
  Menu,
  ChevronLeft,
  Settings,
  PieChart,
  Briefcase,
  Box,
  Lock as LockIcon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import AdminPinModal from './AdminPinModal';

// --- MENU CONFIGURATIONS ---

const userMenuItems = [
  { key: 'dashboard', path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  {
    key: 'register', icon: User, label: 'Register', hasSubmenu: true,
    submenu: [{ path: '/register', label: 'Apply Now' }]
  },
  {
    key: 'profile', icon: User, label: 'My Profile', hasSubmenu: true,
    submenu: [
      { path: '/profile', label: 'Personal Details' },
      { path: '/profile/kyc', label: 'KYC & Bank Details' },
      { path: '/profile/change-password', label: 'Change Password' },
    ]
  },
  {
    key: 'network', icon: Network, label: 'My Network', hasSubmenu: true,
    submenu: [
      { path: '/network/direct', label: 'My Direct' },
      { path: '/network/downlines', label: 'My Downlines' },
      { path: '/network/report', label: 'Downline Report' },
    ]
  },

  {
    key: 'epin', icon: PackageIcon, label: 'E-Pin System', hasSubmenu: true,
    submenu: [
      { path: '/epin/apply', label: 'Apply New' },
      { path: '/epin/request-report', label: 'Request Report' },
      { path: '/epin/unused', label: 'Unused Pins' },
      { path: '/epin/used', label: 'Used Pins' },
    ]
  },
  {
    key: 'products', icon: Box, label: 'Products', hasSubmenu: true,
    submenu: [
      { path: '/products/packages', label: 'Packages' },
      { path: '/shop', label: 'Shop Products' },
      { path: '/checkout', label: 'Checkout' },
      { path: '/orders', label: 'Shop Orders' },
      { path: '/products/shopping-wallet', label: 'Shopping Wallet' },
      { path: '/products/order-report', label: 'Product Order Report' },
    ]
  },
  {
    key: 'franchise', icon: Briefcase, label: 'Franchise', hasSubmenu: true, requiresApproval: true,
    submenu: [
      { path: '/user/franchise/apply', label: 'Apply for Franchise', alwaysVisible: true },
      { path: '/franchise/buy-epin', label: 'Buy E-Pins', requiresApproval: true },
      { path: '/franchise/transfer', label: 'Transfer Stock', requiresApproval: true },
      { path: '/franchise/stock', label: 'My Inventory', requiresApproval: true },
      { path: '/franchise/sales-report', label: 'Sales Report', requiresApproval: true },
      { path: '/franchise/commission', label: 'Commissions', requiresApproval: true },
      { path: '/franchise/tree', label: 'Team Tree', requiresApproval: true },
      { path: '/franchise/orders', label: 'Customer Orders', requiresApproval: true },
      { path: '/franchise/transactions', label: 'Transactions', requiresApproval: true },
      { path: '/franchise/profile', label: 'Franchise Profile', requiresApproval: true },
    ]
  },

  {
    key: 'income', icon: IndianRupee, label: 'Incomes', hasSubmenu: true,
    submenu: [
      { path: '/earnings', label: 'All Earnings' },
      { path: '/income/wallet', label: 'Income Wallet' },
      { path: '/income/binary', label: 'Binary Income' },
      { path: '/income/matching', label: 'Matching Bonus' },
      { path: '/income/performance', label: 'Performance Bonus' },
      { path: '/income/binary-payout', label: 'Payout History' },
    ]
  },
];

// franchiseMenuItems removed — franchise users now use userMenuItems with franchise submenu unlocked


const adminMenuItems = [
  { key: 'dashboard', path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', requiresPin: true },
  {
    key: 'users', icon: User, label: 'User Ecosystem', hasSubmenu: true, requiresPin: true,
    submenu: [
      { path: '/admin/users', label: 'All Users' },
      { path: '/admin/approvals', label: 'Pending Approvals' },
      { path: '/admin/teams', label: 'Teams & Trees' },
      { path: '/admin/wallet-history', label: 'Wallet History' },
      { path: '/admin/verify-email', label: 'Verify User Email' },
      { path: '/admin/blocked', label: 'Blocked Users' },
    ]
  },
  {
    key: 'finance', icon: IndianRupee, label: 'Financial Ops', hasSubmenu: true, requiresPin: true,
    submenu: [
      { path: '/admin/binary-payouts', label: 'Binary Payouts' },
      { path: '/admin/withdrawals', label: 'Withdrawal Requests' },
      { path: '/admin/reports/payout-history', label: 'Payout History' },
      { path: '/admin/transactions', label: 'Transactions' },
      { path: '/admin/history-week', label: 'History Week' },
    ]
  },
  {
    key: 'products', icon: Box, label: 'Sales & Products', hasSubmenu: true,
    submenu: [
      { path: '/admin/shop-orders', label: 'Shop Orders' },
      { path: '/admin/products/list', label: 'Products List' },
      { path: '/admin/products/categories', label: 'Product Categories' },
      { path: '/admin/products/packages', label: 'Packages Management' },
      { path: '/admin/products/bogo', label: 'BOGO Offers' },
      { path: '/admin/landing-products', label: 'Landing Products' },
    ]
  },
  {
    key: 'franchises', icon: Briefcase, label: 'Franchise Network', hasSubmenu: true, requiresPin: true,
    submenu: [
      { path: '/admin/franchises', label: 'All Franchises' },
      { path: '/admin/create-franchise', label: 'Create Franchise' },
      { path: '/admin/franchise-approvables', label: 'Franchise Applications' },
      { path: '/admin/user-franchise-applications', label: 'User Applications' },
      { path: '/admin/franchise-tree', label: 'Franchise Tree' },
      { path: '/admin/franchise-packages', label: 'Franchise Packages' },
    ]
  },
  {
    key: 'epin', icon: PackageIcon, label: 'E-Pin System', hasSubmenu: true, requiresPin: true,
    submenu: [
      { path: '/admin/epin-requests', label: 'E-Pin Requests' },
    ]
  },
  {
    key: 'billing_reports', icon: PieChart, label: 'Billing & Reports', hasSubmenu: true, requiresPin: true,
    submenu: [
      { path: '/admin/invoices', label: 'All Invoices' },
      { path: '/admin/bill-settings', label: 'Bill Template Settings' },
      { path: '/admin/reports/sales', label: 'Sales Report' },
      { path: '/admin/reports/income', label: 'Income Report' },
      { path: '/admin/reports/tds', label: 'TDS Master Report', isDrawer: true },
    ]
  },
  {
    key: 'system', icon: Settings, label: 'Comm. & System', hasSubmenu: true, requiresPin: true,
    submenu: [
      { path: '/admin/contact-requests', label: 'Contact Requests' },
      { path: '/admin/notices', label: 'Notice Board' },
      { path: '/admin/settings', label: 'System Settings' },
    ]
  }
];


const Sidebar = ({ isOpen, onClose, onOpen, onOpenTds }) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [franchiseApproved, setFranchiseApproved] = useState(false);
  const [isPanelUnlocked, setIsPanelUnlocked] = useState(sessionStorage.getItem('adminPanelUnlocked') === 'true');
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Determine role and theme color
  const role = user?.role?.toLowerCase() || 'user';
  let currentMenuItems = userMenuItems;
  let themeColor = 'blue'; // blue, purple

  if (role === 'admin') {
    currentMenuItems = adminMenuItems;
    themeColor = 'purple';
  }
  // Franchise users stay in the user panel with franchise submenu items unlocked

  // Fetch franchise approval status for regular users
  // If role is already 'franchise', they are approved
  useEffect(() => {
    if (role === 'franchise') {
      setFranchiseApproved(true);
      return;
    }
    if (role === 'user') {
      const token = localStorage.getItem('authToken');
      if (!token) return; // Skip if no token

      fetch(`${API_BASE_URL}/user/franchise_status.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setFranchiseApproved(data.approved || data.status === 'approved' || false);
          }
        })
        .catch(err => console.error('Failed to fetch franchise status:', err));
    }
  }, [role]);

  // Pre-expand menus
  useEffect(() => {
    currentMenuItems.forEach(item => {
      if (item.hasSubmenu && item.submenu) {
        if (item.submenu.some(subItem => subItem.path === location.pathname)) {
          setExpandedMenus(prev => ({ ...prev, [item.key]: true }));
        }
      }
    });
  }, [location.pathname, currentMenuItems]);

  const toggleMenu = useCallback((key) => setExpandedMenus(prev => ({ ...prev, [key]: !prev[key] })), []);
  const handleLogout = useCallback(() => {
    const isOwnerOrAdmin = role === 'admin';
    const target = isOwnerOrAdmin ? '/admin-login' : '/login';

    // Navigate first to exit ProtectedRoute
    navigate(target);
    onClose();

    // Defer state update to allow navigation to take effect
    // This prevents ProtectedRoute from redirecting to /login before we reach /admin-login
    setTimeout(() => {
      logout();
    }, 100);
  }, [logout, navigate, onClose, role]);
  const handleMenuClick = useCallback(() => { if (window.innerWidth < 1024) onClose(); }, [onClose]);

  const handleAdminAction = useCallback((item, isSubmenu = false) => {
    if (role === 'admin' && item.requiresPin && !isPanelUnlocked) {
      setPendingAction({ item, isSubmenu });
      setShowPinModal(true);
      return false;
    }
    return true;
  }, [role, isPanelUnlocked]);

  const onPinSuccess = useCallback(() => {
    setIsPanelUnlocked(true);
    sessionStorage.setItem('adminPanelUnlocked', 'true');
    if (pendingAction) {
      if (pendingAction.isSubmenu) {
        toggleMenu(pendingAction.item.key);
      } else {
        navigate(pendingAction.item.path);
        handleMenuClick();
      }
      setPendingAction(null);
    }
  }, [pendingAction, navigate, toggleMenu, handleMenuClick]);

  // Helper for dynamic classes
  const getActiveClass = (isActive) => {
    if (!isActive) return `hover:bg-${themeColor}-50 text-gray-600 hover:text-${themeColor}-700`;
    // Active State
    if (themeColor === 'orange') return 'bg-orange-600 text-white shadow-md shadow-orange-200';
    if (themeColor === 'purple') return 'bg-purple-600 text-white shadow-md shadow-purple-200';
    return 'bg-blue-600 text-white shadow-md shadow-blue-200';
  };

  const getSubMenuActiveClass = (isActive) => {
    if (!isActive) return 'text-gray-500 hover:text-gray-900 border-l-2 border-transparent';
    if (themeColor === 'orange') return 'text-orange-600 font-bold border-l-2 border-orange-600 bg-orange-50';
    if (themeColor === 'purple') return 'text-purple-600 font-bold border-l-2 border-purple-600 bg-purple-50';
    return 'text-blue-600 font-bold border-l-2 border-blue-600 bg-blue-50';
  };


  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && <div className="fixed inset-0 bg-black/40 z-[45] lg:hidden backdrop-blur-sm" onClick={onClose} />}

      {/* Collapsed Sidebar Toggle Button - Hidden on mobile when sidebar is open */}
      {!isOpen && (
        <button
          onClick={onOpen}
          className="fixed left-0 top-20 sm:top-24 z-[48] p-2 pl-3 bg-white shadow-lg rounded-r-xl border border-gray-100 text-gray-600 hover:text-blue-600 transition-all hidden lg:flex"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Sidebar - Responsive */}
      <div className={`w-72 sm:w-80 lg:w-72 bg-white fixed left-0 top-16 md:top-[75px] bottom-0 shadow-2xl z-[48] transition-transform duration-300 flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-12'
        }`}>

        {/* Header Profile Card - COMPACT & Responsive */}
        <div className={`p-3 sm:p-4 bg-gradient-to-br ${themeColor === 'orange' ? 'from-orange-50 to-amber-50' : themeColor === 'purple' ? 'from-purple-50 to-pink-50' : 'from-blue-50 to-indigo-50'} ${!isOpen ? 'lg:hidden' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className={`p-1.5 sm:p-2 rounded-lg bg-white shadow-sm text-${themeColor}-600`}>
                {role === 'admin' ? <Settings size={20} /> : <User size={20} />}
              </div>
              <span className="font-bold text-gray-900 text-xs sm:text-sm uppercase tracking-wide">
                {role === 'admin' ? 'Administrator' : role === 'franchise' ? 'Direct Seller ★' : 'Direct Seller'}
              </span>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/50 rounded-full transition lg:hidden">
              <ChevronLeft size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Scrollable Menu */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-1 custom-scrollbar">
          {currentMenuItems.map(item => (
            <div key={item.key}>
              {item.hasSubmenu ? (
                <div>
                  <button
                    onClick={(e) => {
                      if (handleAdminAction(item, true)) {
                        toggleMenu(item.key);
                      }
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 mb-1 
                                    ${expandedMenus[item.key] ? `bg-${themeColor}-50 text-${themeColor}-700` : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-3">
                      {item.icon ? <item.icon size={18} strokeWidth={2} /> : <Box size={18} strokeWidth={2} />}
                      <span>{item.label}</span>
                      {role === 'admin' && item.requiresPin && !isPanelUnlocked && <LockIcon size={12} className="text-gray-400" />}
                    </div>
                    <ChevronDown size={16} className={`transition-transform duration-200 ${expandedMenus[item.key] ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Submenu */}
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedMenus[item.key] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="ml-4 space-y-1 py-1">
                      {item.submenu.map((sub, idx) => {
                        // Check if this item requires franchise approval
                        const requiresApproval = sub.requiresApproval && !franchiseApproved;
                        const isAlwaysVisible = sub.alwaysVisible;

                        // Show item if it's always visible OR if it doesn't require approval OR if franchise is approved
                        if (isAlwaysVisible || !sub.requiresApproval || franchiseApproved) {
                          if (sub.isDrawer) {
                            return (
                              <button
                                key={idx}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (handleAdminAction(item, true)) {
                                    onOpenTds();
                                    handleMenuClick();
                                  }
                                }}
                                className={`w-full text-left block pl-9 py-2 rounded-r-lg text-sm transition-colors ${getSubMenuActiveClass(false)}`}
                              >
                                {sub.label}
                              </button>
                            );
                          }
                          return (
                            <NavLink
                              key={idx}
                              to={sub.path}
                              onClick={handleMenuClick}
                              className={({ isActive }) => `block pl-9 py-2 rounded-r-lg text-sm transition-colors ${getSubMenuActiveClass(isActive)}`}
                            >
                              {sub.label}
                            </NavLink>
                          );
                        } else if (requiresApproval) {
                          // Show locked item
                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between pl-9 py-2 rounded-r-lg text-sm text-gray-400 cursor-not-allowed border-l-2 border-transparent"
                              title="Franchise approval required"
                            >
                              <span>{sub.label}</span>
                              <span className="text-xs">🔒</span>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  to={item.path}
                  onClick={(e) => {
                    if (!handleAdminAction(item)) {
                      e.preventDefault();
                    } else {
                      handleMenuClick();
                    }
                  }}
                  className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 mb-1 ${getActiveClass(isActive)}`}
                >
                  {item.icon ? <item.icon size={18} strokeWidth={2} /> : <Box size={18} strokeWidth={2} />}
                  <span>{item.label}</span>
                  {role === 'admin' && item.requiresPin && !isPanelUnlocked && <LockIcon size={12} className="ml-auto text-gray-400" />}
                </NavLink>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 space-y-2">
          {role === 'admin' && isPanelUnlocked && (
            <button
              onClick={() => {
                setIsPanelUnlocked(false);
                sessionStorage.removeItem('adminPanelUnlocked');
              }}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all text-xs font-bold"
            >
              <LockIcon size={14} />
              <span>Lock Panel</span>
            </button>
          )}
          <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all text-sm font-semibold group">
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>

      </div>

      <AdminPinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onSuccess={onPinSuccess}
      />
    </>
  );
};

export default Sidebar;


